import { EMAIL_PATTERN, toExternalHref } from '@/utils/externalHref';

/**
 * BLS-406: helpers for the contact details and links i4 showed under a sale
 * catalogue (Email, Homepage, three "extra info" links) and for the URLs kept
 * in plain-text sale notes.
 *
 * All of these are free text typed into i4, not validated URLs: an email field
 * can hold "a@b.com; c@d.com" or "Name <a@b.com>", a homepage can be a bare
 * domain, and an extra-info "URL" is often "#<saleNo>" with a phone number as
 * its label. Hrefs come from the shared `toExternalHref`, so only http(s)
 * and mailto are produced.
 */

export type TextSegment =
  { kind: 'text'; text: string } | { kind: 'link'; text: string; href: string };

export type SaleInfoLink =
  | { kind: 'external'; text: string; href: string }
  | { kind: 'sale'; text: string; saleId: number }
  | { kind: 'text'; text: string };

// URLs (host must contain a dot, so "http://angusnz/Glen" stays text), www.
// addresses and emails inside running text. ")" is excluded so the
// "label (https://...)" form written by RPT.f_StripHtmlNotes links cleanly.
const WEB_IN_TEXT = /https?:\/\/[^\s<>()"'/]+\.[^\s<>()"']+/;
const WWW_IN_TEXT = /www\.[^\s<>()"'/]+\.[^\s<>()"']+/;
const LINK_IN_TEXT = new RegExp(
  `${WEB_IN_TEXT.source}|${WWW_IN_TEXT.source}|${EMAIL_PATTERN.source}`,
  'gi',
);
const TRAILING_PUNCTUATION = /[.,;:!?]+$/;

/** Subject line i4 put on sale enquiry emails. */
export function saleEnquirySubject(saleName: string | null | undefined) {
  const name = saleName?.trim();
  return name ? `Web Enquiry: ${name}` : undefined;
}

/** "https://www.x.com.au/" -> "www.x.com.au" for link text. */
function displayUrl(value: string) {
  return value
    .trim()
    .replace(/^mailto:/i, '')
    .replace(/^https?:\/\//i, '')
    .replace(/\/$/, '');
}

/** Splits free text into plain runs and http(s) / www / email links. */
export function linkifyText(
  text: string | null | undefined,
  emailSubject?: string,
): TextSegment[] {
  if (!text) return [];
  const segments: TextSegment[] = [];
  let last = 0;

  for (const match of text.matchAll(LINK_IN_TEXT)) {
    const start = match.index ?? 0;
    const raw = match[0];
    const linkText = raw.replace(TRAILING_PUNCTUATION, '');
    const href = toExternalHref(linkText, emailSubject);
    if (!href) continue;

    if (start > last) {
      segments.push({ kind: 'text', text: text.slice(last, start) });
    }
    segments.push({ kind: 'link', text: linkText, href });
    last = start + linkText.length;
  }

  if (last < text.length) {
    segments.push({ kind: 'text', text: text.slice(last) });
  }
  return segments;
}

/**
 * Email / Homepage field: one link when the whole value is an address
 * (including a bare domain), otherwise the links found inside the text.
 */
export function linkifyField(
  value: string | null | undefined,
  emailSubject?: string,
): TextSegment[] {
  const v = value?.trim() ?? '';
  if (!v) return [];
  const href = toExternalHref(v, emailSubject);
  if (href) return [{ kind: 'link', text: displayUrl(v), href }];
  return linkifyText(v, emailSubject);
}

/**
 * One of i4's three extra-info links. i4 rendered "#<saleNo>" as a link to
 * that sale's catalogue; a bare "#" or the current sale's own number just
 * carried contact text in the label.
 */
export function resolveSaleInfoLink(
  url: string | null | undefined,
  label: string | null | undefined,
  currentSaleId: number | null | undefined,
): SaleInfoLink | null {
  const u = url?.trim() ?? '';
  const l = label?.trim() ?? '';
  if (!u && !l) return null;

  if (u.startsWith('#')) {
    const saleId = Number(u.slice(1).trim());
    if (Number.isInteger(saleId) && saleId > 0 && saleId !== currentSaleId) {
      return { kind: 'sale', saleId, text: l || `Sale ${saleId}` };
    }
    return l ? { kind: 'text', text: l } : null;
  }

  const href = toExternalHref(u);
  if (href) return { kind: 'external', href, text: l || displayUrl(u) };

  // Relative i4 paths (../../I4_images/...) no longer resolve.
  if (/^[./\\]/.test(u)) return l ? { kind: 'text', text: l } : null;

  // Contact text typed into the URL field ("0407 959 837").
  return { kind: 'text', text: l && u ? `${l}: ${u}` : l || u };
}
